import classNames from 'classnames';
import { EmployeePicker, PanelContent } from 'components';
import SidePanelFilter from 'components/foundation/FilterSidePanel/FilterSidePanel';
import BasicPicker from 'components/foundation/pickers/basic-picker/BasicPicker/BasicPicker';
import { useQueryParams } from 'hooks';
import { t } from 'i18next';
import { useState } from 'react';
import { ClassName } from 'types/ClassName';
import { QueryParam } from 'utils/constants/queryParam';
import { anyFilterIsSet, filterHasChanged } from 'utils/filterUtils';
import { CertificationStatusReportTabType } from '../CertificationStatusReportTabHeader/CertificationStatusReportTabHeader';
import CertificationPicker from 'components/domain/certification/CertificationPicker/CertificationPicker';

export enum CertificationsExpirationOptions {
  SEVEN_DAYS = 'sevenDays',
  THIRTY_DAYS = 'thirtyDays',
  SIXTY_DAYS = 'sixtyDays',
  NINETY_DAYS = 'ninetyDays',
  NONE = 'none',
}

export interface ICertificationStatusReportFilterProps {
  className?: ClassName;
}

export interface ICertificationStatusReportFilterData {
  memberId: string | null;
  certificationId: string | null;
  expirationDate: CertificationsExpirationOptions | null;
  reportType: CertificationStatusReportTabType;
}

const CertificationStatusReportFilter = (props: ICertificationStatusReportFilterProps) => {
  const { className } = props;
  const { getParam, updateParams } = useQueryParams();

  const currentMemberId = getParam(QueryParam.EMPLOYEE_ID);
  const currentCertificationId = getParam(QueryParam.CERTIFICATION_ID);
  const currentExpirationDate: CertificationsExpirationOptions | null = getParam(
    QueryParam.CERTIFICATION_EXPIRATION_DATE
  );
  const currentReportType: CertificationStatusReportTabType =
    getParam(QueryParam.CERTIFICATION_REPORT_TYPE) ?? CertificationStatusReportTabType.ACTIVE;

  const [data, setData] = useState<ICertificationStatusReportFilterData>({
    memberId: currentMemberId ?? null,
    certificationId: currentCertificationId ?? null,
    expirationDate: currentExpirationDate ?? null,
    reportType: currentReportType,
  });

  const expirationOptions = [
    { id: CertificationsExpirationOptions.SEVEN_DAYS, name: t('Expires < 7 Days') },
    { id: CertificationsExpirationOptions.THIRTY_DAYS, name: t('Expires < 30 Days') },
    { id: CertificationsExpirationOptions.SIXTY_DAYS, name: t('Expires < 60 Days') },
    { id: CertificationsExpirationOptions.NINETY_DAYS, name: t('Expires < 90 Days') },
    { id: CertificationsExpirationOptions.NONE, name: t('No Expiration Date') },
  ];

  const handleApply = () => {
    const params = {
      [QueryParam.EMPLOYEE_ID]: data.memberId ?? null,
      [QueryParam.CERTIFICATION_ID]: data.certificationId ?? null,
      [QueryParam.CERTIFICATION_EXPIRATION_DATE]: data.expirationDate ?? null,
    };

    updateParams(params);
  };

  const handleReset = () => {
    setData({
      ...data,
      memberId: null,
      certificationId: null,
      expirationDate: null,
    });

    const params = {
      [QueryParam.EMPLOYEE_ID]: null,
      [QueryParam.CERTIFICATION_ID]: null,
      [QueryParam.CERTIFICATION_EXPIRATION_DATE]: null,
    };

    updateParams(params);
  };

  const hasChanged = () => {
    const current = {
      memberId: currentMemberId,
      certificationId: currentCertificationId,
      expirationDate: currentExpirationDate,
    };

    return filterHasChanged(current, data);
  };

  const anyFilterSet = () => {
    const ignoredProperties: (keyof ICertificationStatusReportFilterData)[] = ['reportType'];
    if (currentReportType == CertificationStatusReportTabType.EXPIRED) {
      ignoredProperties.push('expirationDate');
    }
    return anyFilterIsSet<ICertificationStatusReportFilterData>(data, ignoredProperties);
  };

  const onSelectField =
    <K extends keyof ICertificationStatusReportFilterData, V extends ICertificationStatusReportFilterData[K]>(
      key: keyof ICertificationStatusReportFilterData
    ) =>
    (value: V) => {
      setData({ ...data, [key]: value });
    };

  const classes = classNames('certification-status-report-filter', className);

  return (
    <SidePanelFilter
      onApply={handleApply}
      onClear={handleReset}
      appliable={hasChanged()}
      clearable={anyFilterSet()}
      className={classes}
      buttonsClassName={'mx-5'}
    >
      <PanelContent flex={false}>
        <div className="p-5">
          <h3>{t('Filter')}</h3>
          <div className={'py-5'}>
            <EmployeePicker
              onSelect={onSelectField('memberId')}
              value={data.memberId}
              placeholder={t('Employee')}
              className={'mb-5'}
            />
            <CertificationPicker
              value={data.certificationId}
              onSelect={onSelectField('certificationId')}
              placeholder={t('Certification')}
              className={'mb-5'}
            />
            {currentReportType == CertificationStatusReportTabType.ACTIVE && (
              <BasicPicker
                value={data.expirationDate}
                onSelect={(value) => onSelectField('expirationDate')(value)}
                placeholder={t('Expiration Date')}
                className={'mb-5'}
                data={expirationOptions}
              />
            )}
          </div>
        </div>
      </PanelContent>
    </SidePanelFilter>
  );
};

export default CertificationStatusReportFilter;
